import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import useDebounce from '../../hooks/useDebounce.ts';
import dataMatrix from '../../utilities/dataMatrixCodec';
import { toast } from '../../func.js';

interface IProps {
  id?: string;
  isLoading: boolean;
  onScan: (data: { [key: string]: string }) => void;
  value?: string | number;
  disabled?: boolean;
  allowPassiveInput?: boolean;
}

const DataMatrixInput = ({
  onScan,
  id = '_' + crypto.randomUUID(),
  value,
  disabled = false,
  allowPassiveInput = false,
  isLoading,
}: IProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const resetPassiveBarcode = useRef<NodeJS.Timeout>(null);
  const nonInputBarcodeRef = useRef<string>('');

  const [barcode, setBarcode] = useState<string>('');
  const [groupSeparatorIndices, setGroupSeparatorIndices] = useState<number[]>(
    []
  );
  const [nonInputGroupSeparatorIndices, setNonInputGroupSeparatorIndices] =
    useState<number[]>([]);

  const [isHumanInput, setIsHumanInput] = useState<boolean>(false);
  const [isEnterKey, setIsEnterKey] = useState<boolean>(false);
  const [lastKeyStroke, setLastKeyStroke] = useState<number>();

  const calcIsHumanInput = useCallback(() => {
    const now = new Date().getTime();
    if (lastKeyStroke) {
      const timeBetweenKeyStrokes = now - lastKeyStroke;
      setIsHumanInput(timeBetweenKeyStrokes > 50);
      setLastKeyStroke(now);
      return;
    }
    setLastKeyStroke(now);
  }, [lastKeyStroke, setIsHumanInput]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (isLoading) return;

      const { ctrlKey, shiftKey, key, target } = event;
      const { current: nonInputBarcode } = nonInputBarcodeRef;
      const isInputElementFocused = target === ref.current;

      if (ctrlKey && key === ']') {
        setGroupSeparatorIndices([...groupSeparatorIndices, barcode.length]);
        setNonInputGroupSeparatorIndices([
          ...nonInputGroupSeparatorIndices,
          nonInputBarcode.length,
        ]);
      }
      setIsEnterKey(
        key.toLocaleLowerCase() === 'enter' || key.toLocaleLowerCase() === 'tab'
      );

      if (key.length === 1 && /[a-zA-Z0-9]/.test(key) && !isInputElementFocused)
        nonInputBarcodeRef.current += key;

      calcIsHumanInput();

      if (ctrlKey && shiftKey && key.toLocaleLowerCase() === 'j')
        toast({
          payload: {
            msg: `Your scanner is configured to terminate with "LF" or "CRLF".<br />
          Please set to "CR" or "No Terminator."`,
            class: 'orange white-text',
            displayLength: 10000,
          },
        });
    },
    [barcode, calcIsHumanInput]
  );

  const startResetTimer = () => {
    resetPassiveBarcode.current = setInterval(() => {
      nonInputBarcodeRef.current = '';
    }, 500);
  };

  useLayoutEffect(() => {
    const listener = (event: globalThis.KeyboardEvent) => {
      if (event.target !== ref.current)
        onKeyDown(event as unknown as KeyboardEvent<HTMLInputElement>);
    };

    if (allowPassiveInput) {
      startResetTimer();
      document.addEventListener('keydown', listener);
      return () => {
        if (resetPassiveBarcode.current)
          clearInterval(resetPassiveBarcode.current);
        document.removeEventListener('keydown', listener);
      };
    }
  });

  const debouncedDecode = useDebounce(
    (barcode: string, isHumanInput: boolean, isEnterKey: boolean) => {
      if (resetPassiveBarcode.current)
        clearInterval(resetPassiveBarcode.current);
      let newGSIndices: number[] = [];

      if (isHumanInput && !isEnterKey) {
        nonInputBarcodeRef.current = '';
        setNonInputGroupSeparatorIndices([]);
        startResetTimer();
        return;
      }

      try {
        const { current: nonInputBarcode } = nonInputBarcodeRef;
        const trimmedBarcode = barcode.trim();
        const trimmedNonInputBarcode = nonInputBarcode.trim();
        const newBarcode = trimmedBarcode || trimmedNonInputBarcode;

        newGSIndices = trimmedBarcode
          ? groupSeparatorIndices
          : nonInputGroupSeparatorIndices;

        if (!newBarcode) return;

        if (newBarcode.length < 20) return onScan({ unknown: trimmedBarcode });

        const barcodeAsArray = newBarcode.split('');
        newGSIndices.forEach(index => {
          barcodeAsArray.splice(index, 0, '{{GS}}');
        });

        const result = dataMatrix.decode(barcodeAsArray.join(''), '{{GS}}');
        onScan(result as { [key: string]: string });
      } catch (err) {
        if (!isHumanInput && !newGSIndices.length)
          return toast({
            payload: {
              msg: 'You must enable the "Function Key Mapping" feature on your scanner!',
              class: 'orange white-text',
              displayLength: 10000,
            },
          });

        onScan({ unknown: barcode });
      } finally {
        setBarcode('');
        setGroupSeparatorIndices([]);
        nonInputBarcodeRef.current = '';
        setNonInputGroupSeparatorIndices([]);
        startResetTimer();
      }
    },
    500
  );

  useEffect(() => {
    if (resetPassiveBarcode.current) clearInterval(resetPassiveBarcode.current);
    debouncedDecode(barcode, isHumanInput, isEnterKey);
  }, [barcode, isHumanInput, isEnterKey, nonInputBarcodeRef.current]);

  return (
    <input
      ref={ref}
      id={id}
      value={barcode || value || ''}
      onChange={({ target: { value } }) => setBarcode(value)}
      onKeyDown={onKeyDown}
      type="text"
      disabled={disabled || isLoading}
    />
  );
};

export default DataMatrixInput;
